import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './bpm';
import logoOliverSmall from './img/Logo Oliver small.png';
import './LoginPage.css';

const LoginPage = () => {
  const { login: loginGoogle, loginWithEmail, authError, clearError } = useAuth();
  const { showToast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loginMethod, setLoginMethod] = useState(null); // null, 'email', ou 'google'
  const lastErrorRef = useRef(null); // Mémoriser la dernière erreur affichée
  const connectionTestedRef = useRef(false); // Éviter de tester plusieurs fois

  // Tester la connexion au backend au chargement de la page
  useEffect(() => {
    if (!connectionTestedRef.current) {
      connectionTestedRef.current = true;
      // Test silencieux : /api/ping (pas de DB) — /api/health faisait DB+Google et pouvait monopoliser le pool SQL (1 connexion) et retarder le login
      const apiBaseUrl = process.env.REACT_APP_API_URL || 
                        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000/api');
      fetch(`${apiBaseUrl}/ping`, { 
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache'
      })
        .then(() => {})
        .catch(error => {
          if (process.env.NODE_ENV === 'development') {
            console.warn('⚠️ Backend non accessible depuis le navigateur:', error);
          }
        });
    }
  }, []);

  // Icône pour les toasts "Connexion" (session terminée / token expiré)
  const connexionToastIcon = '<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#1f1f1f"><path d="M232-172q-26 0-43-17t-17-43v-114h28v114q0 12 10 22t22 10h496q12 0 22-10t10-22v-496q0-12-10-22t-22-10H232q-12 0-22 10t-10 22v114h-28v-114q0-26 17-43t43-17h496q26 0 43 17t17 43v496q0 26-17 43t-43 17H232Zm206-164-20-20 110-110H172v-28h356L418-604l20-20 144 144-144 144Z"/></svg>';

  // Afficher le message si déconnexion suite à connexion depuis un autre appareil
  useEffect(() => {
    try {
      const reason = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('auth_logout_reason') : null;
      if (reason) {
        sessionStorage.removeItem('auth_logout_reason');
        showToast(reason, 'info', 5000, 'Connexion', 'Connexion', connexionToastIcon);
      }
    } catch (_) { /* ignorer */ }
  }, [showToast]);

  // Afficher les erreurs dans un toast au lieu d'un message sur la page
  // Utiliser une référence pour éviter d'afficher le même toast plusieurs fois
  useEffect(() => {
    if (authError && authError !== lastErrorRef.current) {
      lastErrorRef.current = authError;
      const isDbOrService =
        /trop de connexions|base de données|postgresql|service.*indisponible|temporairement surchargé/i.test(
          authError || ''
        );
      showToast(
        authError,
        'error',
        5000,
        isDbOrService ? 'Service temporairement indisponible' : 'Erreur d\'authentification',
        null,
        null
      );
    } else if (!authError) {
      // Réinitialiser la référence quand l'erreur est effacée
      lastErrorRef.current = null;
    }
  }, [authError, showToast]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      return;
    }
    setIsLoading(true);
    clearError();
    try {
      await loginWithEmail(email, password);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    clearError();
    loginGoogle();
  };

  return (
    <div className="login-page">
      <div className="login-container">
        {/* Logo */}
        <div className="login-logo">
          <img 
            src={
              typeof localStorage !== 'undefined' && localStorage.getItem('sidebarLogo')
                ? localStorage.getItem('sidebarLogo')
                : logoOliverSmall
            } 
            alt="Logo" 
          />
        </div>

        {/* Titre */}
        <h1 className="login-title">Portfolio Manager</h1>
        <p className="login-subtitle">Suivez et gérez votre portefeuille boursier</p>

        {/* Choix de la méthode de connexion */}
        {!loginMethod && (
          <div className="login-methods">
            <button 
              className="login-method-button" 
              onClick={() => setLoginMethod('email')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" style={{ marginRight: '12px' }}>
                <path fill="currentColor" d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
              </svg>
              Se connecter avec votre e-mail
            </button>
            
            <div className="login-divider">
              <span>ou</span>
            </div>

            <button 
              className="login-method-button login-button-google" 
              onClick={handleGoogleLogin}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" style={{ marginRight: '12px' }}>
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Se connecter avec Google
            </button>
          </div>
        )}

        {/* Formulaire de connexion email */}
        {loginMethod === 'email' && (
          <form className="login-form" onSubmit={handleEmailLogin}>
            <div className="login-form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Votre adresse mail"
                autoComplete="email"
                required
                disabled={isLoading}
              />
            </div>
            
            <div className="login-form-group">
              <label htmlFor="password">Mot de passe</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
                disabled={isLoading}
              />
            </div>

            <div className="login-form-actions">
              <button 
                type="button" 
                className="login-back-button"
                onClick={() => {
                  setLoginMethod(null);
                  setEmail('');
                  setPassword('');
                  clearError();
                }}
                disabled={isLoading}
              >
                Retour
              </button>
              <button 
                type="submit" 
                className="login-submit-button"
                disabled={isLoading || !email || !password}
              >
                {isLoading ? 'Connexion...' : 'Se connecter'}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Pied de page - en dehors du container */}
      <footer className="login-footer">
        <a href="/conditions" className="login-footer-link">
          Conditions d'utilisation du site
        </a>
        <span className="login-footer-separator">•</span>
        <a href="/contact" className="login-footer-link">
          Contact
        </a>
      </footer>
    </div>
  );
};

export default LoginPage;


